import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { EMPTY_GUID } from '@core/constants';
import { AuthorizationIcd, ClinicalProtocol, ICDOriginalCode } from '@core/model';
import { AuthenticationService, AuthorizationService, LookupService } from '@core/service';
import { AuthorizationData, ICDCode, ICDOrderType } from '@shared/model';
import { SecurityService } from '@mynexus/mynexus-ui-lib';
import moment from 'moment';
import { MessageService, SelectItem } from 'primeng/api';
import { SubSink } from 'subsink';
import { PRIMARY_ICD_MESSAGE } from '@core/constants/ICD';
import { ListboxClickEvent } from 'primeng/listbox';

@Component({
    selector: 'app-diagnosis-new',
    templateUrl: './diagnosis-new.component.html',
    styleUrls: ['./diagnosis-new.component.scss'],
    standalone: false
})
export class DiagnosisNewComponent implements OnInit {
  isICDAddedRemoved = false;
  @Input() tenantId: string;
  @Input() authorizationDetail: AuthorizationData;
  @Output() save: EventEmitter<AuthorizationIcd[]> = new EventEmitter<AuthorizationIcd[]>();

  searchForm: FormGroup;
  diagnosisForm: FormGroup;
  isPrimaryPdgm = true;
  primaryICDisNotPDGM = '';
  isLoading = false;
  subs = new SubSink();
  icdSearchResultsList: ICDCode[] = [];
  selectedIcdCodesList: ICDCode[] = [];
  icdResp: ICDCode[] = [];
  availableIcds: SelectItem[] = [];
  selectedIcds: SelectItem[] = [];
  selectedIcd: SelectItem[] = [];
  unknownIcdCode = 'Unknown';
  canAddMoreIcds = true;
  private orderTypeList: ICDOrderType[] = ['Primary', 'Secondary', 'Tertiary', 'Quaternary'];
  isExternalUser: boolean;
  viewOnly: boolean;
  private readonly emptyGuid: string = EMPTY_GUID;
  icdlist: AuthorizationIcd[] = [];
  saveICD: string;

  get originalCodes(): ICDOriginalCode[] {
    const codes: ICDOriginalCode[] = [];

    if (!this.authorizationDetail.authorization) {
      return codes;
    }

    if (this.authorizationDetail.authorization.originalPrimaryICDCode) {
      codes.push({
        code: this.authorizationDetail.authorization.originalPrimaryICDCode,
        description: this.authorizationDetail.authorization.originalPrimaryICDDescription,
        orderType: 'Primary'
      });
    }

    if (this.authorizationDetail.authorization.originalSecondaryICDCode) {
      codes.push({
        code: this.authorizationDetail.authorization.originalSecondaryICDCode,
        description: this.authorizationDetail.authorization.originalSecondaryICDDescription,
        orderType: 'Secondary'
      });
    }

    if (this.authorizationDetail.authorization.originalTertiaryICDCode) {
      codes.push({
        code: this.authorizationDetail.authorization.originalTertiaryICDCode,
        description: this.authorizationDetail.authorization.originalTertiaryICDDescription,
        orderType: 'Tertiary'
      });
    }

    if (this.authorizationDetail.authorization.originalQuaternaryICDCode) {
      codes.push({
        code: this.authorizationDetail.authorization.originalQuaternaryICDCode,
        description: this.authorizationDetail.authorization.originalQuaternaryICDDescription,
        orderType: 'Quaternary'
      });
    }

    return codes;
  }

  constructor(
    private readonly authenticationService: AuthenticationService,
    private readonly messageService: MessageService,
    private readonly authorizationSvc: AuthorizationService,
    private readonly lookupService: LookupService,
    private readonly securityService: SecurityService
  ) {
  }

  ngOnInit() {
    this.viewOnly = this.authenticationService.isExternalUser || !this.securityService.hasAccess(['Diagnosis.Write']) || this.authenticationService.isClaims;
    if (this.authorizationDetail && this.authorizationDetail.authorizationIcdDTOs) {
      // Get Global Values
      this.setGlobalVariables();
      // Build the Form
      this.buildDiagnosisForm();
      // Set default values
      this.setDefaultValues();
    }
  }

  setGlobalVariables() {
    this.isExternalUser = this.authenticationService.isExternalUser;
    this.primaryICDisNotPDGM = this.isExternalUser ? PRIMARY_ICD_MESSAGE.EXTERNAL : PRIMARY_ICD_MESSAGE.INTERNAL;
  }

  buildDiagnosisForm() {
    this.searchForm = new FormGroup({
      icdSearch: new FormControl('', [Validators.required, Validators.minLength(3), Validators.maxLength(10)])
    });
    this.diagnosisForm = new FormGroup({
      selectedIcd: new FormControl([])
    });
  }

  setDefaultValues() {
    if (this.authorizationDetail.authorizationIcdDTOs && this.authorizationDetail.authorizationIcdDTOs.length > 0) {
      const icds: ICDCode[] = [];
      this.isLoading = true;
      this.authorizationDetail.authorizationIcdDTOs.forEach(e => {
        this.subs.sink = this.lookupService.getIcdCodeById(this.tenantId, e.icdcodeId).subscribe(resp => {
          if (resp) {
            icds.push(resp);
            this.selectedIcdCodesList = [...this.selectedIcdCodesList, resp];
            this.icdSearchResultsList = [...this.icdSearchResultsList, resp];
          }
          if (icds.length === this.authorizationDetail.authorizationIcdDTOs.length) {
            this.sortSelectedIcdsByOrderType(icds);
            this.isLoading = false;
            this.checkPrimaryPDGM();
          }

          this.canAddMoreIcds = resp?.code !== this.unknownIcdCode && icds.length < 4;
        });
      });
    }
  }

  sortSelectedIcdsByOrderType(icds: ICDCode[]) {
    this.orderTypeList.forEach(e => {
      const authIcd = this.authorizationDetail.authorizationIcdDTOs.find(f => f.icdorderType === e.valueOf());
      if (authIcd) {
        const icd = icds.find(f => f.icdCodeID === authIcd.icdcodeId);
        if (icd) {
          this.selectedIcds = [
            ...this.selectedIcds,
            { value: icd.icdCodeID, label: `(${authIcd.icdorderType}) - ${icd.code} ${icd.pdgm ? 'PDGM' : ''} - ${icd.description}` }
          ];
        }
      }
    });
  }

  checkPrimaryPDGM() {
    if (this.selectedIcds.length === 0) {
      this.isPrimaryPdgm = true;
    } else {
      const selectedIcd = this.selectedIcdCodesList.find(e => e.icdCodeID === this.selectedIcds[0].value);
      this.isPrimaryPdgm = selectedIcd.pdgm;
    }
  }

  searchICD() {
    const searchText = this.searchForm.get('icdSearch').value;
    this.isLoading = true;
    this.subs.sink = this.lookupService.getIcdCodes(this.tenantId, searchText).subscribe(resp => {
      if (resp && resp.length > 0) {
        const unknown = this.unknownIcdCode.toLowerCase();
        this.icdResp = resp;
        const filtered = resp.filter((elem) => !this.selectedIcdCodesList.find(({ icdCodeID }) => elem.icdCodeID === icdCodeID));
        this.icdSearchResultsList = this.isExternalUser ? filtered.filter(res => res.code.toLowerCase() !== unknown) : filtered;
        this.sortIcdLists();
        this.setAvailableSelectedIcds(this.icdSearchResultsList);
      } else {
        this.messageService.add(
          { severity: 'warn', summary: 'No ICD Codes Found', detail: 'No ICD Codes were found matching that text.' }
        );
      }
      this.isLoading = false;
    });
  }

  sortIcdLists() {
    if (this.icdSearchResultsList && this.icdSearchResultsList.length > 0) {
      this.icdSearchResultsList.sort((a, b) => a.code.localeCompare(b.code));
    }
  }

  sortAvailableIcdLists() {
    if (this.availableIcds && this.availableIcds.length > 0) {
      this.availableIcds.sort((a, b) => a.label.localeCompare(b.label));
    }
  }

  setAvailableSelectedIcds(icds: ICDCode[]) {
    this.availableIcds = [];
    if (icds && icds.length > 0) {
      icds.forEach(e => {
        if (e.pdgm === true) {
          this.availableIcds.push({ value: e.icdCodeID, label: `${e.code} (PDGM) - ${e.description}` });
        } else {
          this.availableIcds.push({ value: e.icdCodeID, label: `${e.code} - ${e.description}` });
        }
      });
    }
  }

  icdListBoxClick($event: ListboxClickEvent) {
    const icd = $event.option.value;
    const selectedIcd = this.icdResp.find(e => e.icdCodeID === icd);

    if (selectedIcd) {
      const haveUnknown = selectedIcd.code === this.unknownIcdCode;
      if (haveUnknown) {
        this.selectedIcds = [];
        this.selectedIcds.push(this.availableIcds.find(e => e.value === icd));
        this.canAddMoreIcds = false;
      } else {
        this.selectedIcds.push(this.availableIcds.find(e => e.value === icd));
        this.canAddMoreIcds = this.selectedIcds.length < 4;
      }
    }

    this.availableIcds.splice(this.availableIcds.findIndex(e => e.value === icd), 1);
    this.selectedIcdCodesList.push(this.icdResp.find(e => e.icdCodeID === icd));
    this.assignOrderTypeToSelected();
    this.handleAuthorizationIcdChanges();
  }

  assignOrderTypeToSelected() {
    const selectItemList: SelectItem[] = [];
    this.selectedIcds.forEach((e1, index) => {
      const icd = this.selectedIcdCodesList.find(e2 => e2.icdCodeID === e1.value);
      if (icd.pdgm === true) {
        selectItemList.push({ value: icd.icdCodeID, label: `(${this.orderTypeList[index]}) - ${icd.code} (PDGM) - ${icd.description}` });
      } else {
        selectItemList.push({ value: icd.icdCodeID, label: `(${this.orderTypeList[index]}) - ${icd.code} - ${icd.description}` });
      }
    });
    this.selectedIcds = selectItemList;
  }

  icdSelectedListBoxClick(icdId: string) {
    let selectedIcd = this.icdResp.find(e => e.icdCodeID === icdId);
    if (selectedIcd) {
      const haveUnknown = selectedIcd.code === this.unknownIcdCode;
      if (haveUnknown) {
        this.selectedIcds.splice(0);
        this.canAddMoreIcds = true;
      }
    }

    if (this.selectedIcdCodesList) {
      selectedIcd = this.icdResp.find(e => e.icdCodeID === icdId);
      if (selectedIcd) {
        const icd = this.selectedIcdCodesList.find(e => e.icdCodeID === icdId);
        this.availableIcds = [
          ...this.availableIcds,
          { value: icd.icdCodeID, label: `${icd.code} ${icd.pdgm ? 'PDGM' : ''} - ${icd.description}` }
        ];
      }
    }
    this.selectedIcds.splice(this.selectedIcds.findIndex(e => e.value === icdId), 1);
    this.selectedIcdCodesList.splice(this.selectedIcdCodesList.findIndex(e => e.icdCodeID === icdId), 1);
    this.canAddMoreIcds = this.selectedIcds.length < 4;
    this.sortAvailableIcdLists();
    this.assignOrderTypeToSelected();
    this.handleAuthorizationIcdChanges();
  }

  moveSelctedIcdDown(icdId: string) {
    const selectedIndex = this.selectedIcds.findIndex(e => e.value === icdId);
    const temp = this.selectedIcds[selectedIndex];
    this.selectedIcds[selectedIndex] = this.selectedIcds[selectedIndex + 1];
    this.selectedIcds[selectedIndex + 1] = temp;
    this.assignOrderTypeToSelected();
    this.handleAuthorizationIcdChanges();
  }

  moveSelctedIcdUp(icdId: string) {
    const selectedIndex = this.selectedIcds.findIndex(e => e.value === icdId);
    const temp = this.selectedIcds[selectedIndex];
    this.selectedIcds[selectedIndex] = this.selectedIcds[selectedIndex - 1];
    this.selectedIcds[selectedIndex - 1] = temp;
    this.assignOrderTypeToSelected();
    this.handleAuthorizationIcdChanges();
  }

  handleAuthorizationIcdChanges() {
    this.isICDAddedRemoved = (this.selectedIcds.length >= 1) ? true : false;
    this.authorizationDetail.authorizationIcdDTOs = this.createAuthorizationIcdRows();
    this.checkPrimaryPDGM();
  }


  createAuthorizationIcdRows(): AuthorizationIcd[] {
    const timestamp = moment().utc().toISOString();

    this.icdlist = [];
    this.selectedIcds.forEach((e, index) => {
      const dto: AuthorizationIcd = new AuthorizationIcd();
      dto.authorizationIcdid = this.emptyGuid;
      dto.authorizationId = this.authorizationDetail.authorization.authorizationId;
      dto.icdcodeId = e.value;
      dto.icdorderType = this.orderTypeList[index];
      const icdCodeDTO = this.selectedIcdCodesList.find(icdCode => icdCode.icdCodeID === e.value);
      if (icdCodeDTO) {
        dto.icdCodeDTO = icdCodeDTO;
      }
      dto.createdBy = this.authenticationService.getEmail;
      dto.createdOn = timestamp;
      dto.modifiedBy = this.authenticationService.getEmail;
      dto.modifiedOn = timestamp;
      this.icdlist.push(dto);
    });
    return this.icdlist;
  }

  public saveICDCodes(): void {
    this.authorizationSvc.saveAuthorizationIcdList(this.tenantId, this.icdlist).subscribe(
      (res: ClinicalProtocol) => {
        if (res) {
          this.save.emit(this.icdlist);
          this.authorizationDetail.authorization.clinicalGrouping = res.clinicalGrouping;
          this.authorizationDetail.authorization.clinicalGroupingProtocol = res;
          this.isICDAddedRemoved = false;
          this.icdlist = [];
          this.searchForm.reset();
          this.searchForm.controls['icdSearch'].setErrors(null);
          this.availableIcds = [];
          this.messageService.add({ severity: 'success', detail: 'ICD Codes Save Successful', closable: true, life: 4000 });
        }
      });
  }

}
